import React, { useContext, useState } from "react";
import { AppContext } from "../context/app.context";
import { computeEventDiff } from "../utility/computeEventDiff";
import {
  getEffectiveChangedBy,
  stripUsernameDomain,
} from "../utility/computeScheduleDiff";

/**
 * Time Machine #114 Phase 5 — shared rendering component for the History
 * sections of TimeMachineJobModal and TimeMachineCrewModal.
 *
 * Presents a chronological list of EventLog entries (passed in as `events`,
 * already sorted timestamp-desc by the caller). Each row has:
 *   - `_eventKind` tag (📅 Availability for "attribute", 👤 Roster for
 *     "candidate", or no tag if the field isn't present — used by the
 *     crew modal where events come from two collections; jobs use a single
 *     collection so the tag is omitted)
 *   - timestamp (local time + UTC tooltip)
 *   - operation (create / update / delete)
 *   - changedBy username
 *   - source label (Scheduler-UI / External — distinguishes UI clicks from
 *     FileMaker writes)
 *
 * Interactions:
 *   - Single click row → expand in place to show pre/post detail
 *     (full before/after JSON + updateFields if available)
 *   - Double click row → scrub the time-travel slider to that event's
 *     timestamp via setTimeTravelSliderTime, optional onScrubbed callback
 *     to close the parent modal
 *
 * Props:
 *   events           — sorted array of EventLog records
 *   showKindTag      — bool; default false (only crew modal sets true)
 *   onScrubbed       — optional fn() called after a successful double-click scrub;
 *                      typically used by the parent to close the modal after scrub
 *
 * Empty state: a small italic "No history yet" line.
 */
const EventHistoryList = ({ events, showKindTag = false, onScrubbed }) => {
  // Phase 6 follow-up: use the cross-day-aware scrubToTimestamp helper.
  // setTimeTravelSliderTime alone updates only the slider position WITHIN
  // the currently-displayed day's bundle, so double-clicking an event from
  // a different day silently failed (slider value updated but the bundle
  // stayed wrong → schedule appeared not to change). scrubToTimestamp
  // also swaps timeTravelDate when needed, triggering a bundle refetch
  // for the event's day. Falls back to setTimeTravelSliderTime for
  // backward compat with tests that only mock the slider setter.
  const { scrubToTimestamp, setTimeTravelSliderTime } =
    useContext(AppContext) || {};
  const [expandedId, setExpandedId] = useState(null);

  if (!Array.isArray(events) || events.length === 0) {
    return (
      <div
        data-testid="event-history-list-empty"
        className="text-sm italic text-skin-muted py-4"
      >
        No history recorded for this record yet.
      </div>
    );
  }

  const handleClick = (eventId) => {
    setExpandedId((prev) => (prev === eventId ? null : eventId));
  };

  const handleDoubleClick = (timestamp) => {
    // TEMP DIAGNOSTIC: surface every dblclick attempt in the console so
    // we can tell if the browser is actually firing dblclick on the row,
    // whether the timestamp parses, and which scrub path is taken. This
    // log is intentionally noisy; remove once the CrewModal scrub bug
    // is closed.
    console.log(
      "%cEventHistoryList handleDoubleClick:",
      "color: #00FFFF; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
      {
        timestampInput: timestamp,
        timestampType: typeof timestamp,
        hasScrubToTimestamp: typeof scrubToTimestamp === "function",
        hasSetTimeTravelSliderTime: typeof setTimeTravelSliderTime === "function",
        hasOnScrubbed: typeof onScrubbed === "function",
      }
    );
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const dateValid = !isNaN(date.getTime());
    if (!dateValid) {
      console.warn(
        "%cEventHistoryList handleDoubleClick: timestamp parse FAILED — skipping scrub but still closing modal",
        "color: #ff8800; font-weight: bold;",
        timestamp
      );
    } else if (typeof scrubToTimestamp === "function") {
      console.log(
        "%cEventHistoryList handleDoubleClick: calling scrubToTimestamp →",
        "color: #00FF00; font-weight: bold;",
        date.toISOString()
      );
      scrubToTimestamp(date);
    } else if (typeof setTimeTravelSliderTime === "function") {
      // Backward-compat path: same-day scrubs work; cross-day scrubs
      // silently fail (the original Phase 5 behavior).
      console.log(
        "%cEventHistoryList handleDoubleClick: scrubToTimestamp NOT available, falling back to setTimeTravelSliderTime →",
        "color: #ffaa00; font-weight: bold;",
        date.toISOString()
      );
      setTimeTravelSliderTime(date);
    } else {
      console.warn(
        "%cEventHistoryList handleDoubleClick: NEITHER scrubToTimestamp NOR setTimeTravelSliderTime available — skipping scrub but still closing modal",
        "color: #ff0000; font-weight: bold;"
      );
    }
    // ALWAYS fire onScrubbed if available — the parent modal closes whether
    // the scrub succeeded or not. Per user feedback: "if the double click
    // fails and the error message is displayed in the header, then I think
    // the Modal should close as if it worked. Then it's obvious it tried
    // and the error shows why it failed." Without this, a floor-refusal
    // (or any other Lambda-side reject) would leave the modal covering the
    // banner where the error appears, hiding the cause-and-effect.
    if (typeof onScrubbed === "function") onScrubbed();
  };

  return (
    <div data-testid="event-history-list" className="flex flex-col gap-1">
      {events.map((ev) => {
        const id = String(ev._id ?? ev.timestamp);
        const isExpanded = expandedId === id;
        // Phase 6 — audit-row variant. JobHistorySection tags
        // BlackoutDatesAudit records with _rowKind="audit" + a synthetic
        // `timestamp` (audit's invokedAt) and stashes the full audit doc
        // under `_audit`. Render via AuditRow; everything else renders via
        // EventRow as before.
        if (ev._rowKind === "audit") {
          return (
            <AuditRow
              key={id}
              audit={ev._audit}
              timestamp={ev.timestamp}
              isExpanded={isExpanded}
              onClick={() => handleClick(id)}
              onDoubleClick={() => handleDoubleClick(ev.timestamp)}
            />
          );
        }
        return (
          <EventRow
            key={id}
            event={ev}
            isExpanded={isExpanded}
            onClick={() => handleClick(id)}
            onDoubleClick={() => handleDoubleClick(ev.timestamp)}
            showKindTag={showKindTag}
          />
        );
      })}
    </div>
  );
};

const EventRow = ({ event, isExpanded, onClick, onDoubleClick, showKindTag }) => {
  const tsDate = new Date(event.timestamp);
  const tsLocal = isNaN(tsDate.getTime()) ? event.timestamp : tsDate.toLocaleString();
  const tsUtcTitle = isNaN(tsDate.getTime()) ? "" : `UTC: ${tsDate.toISOString()}`;

  const opLabel =
    event.operation === "create"
      ? "Created"
      : event.operation === "update"
        ? "Edited"
        : event.operation === "delete"
          ? "Deleted"
          : event.operation || "?";

  const kindLabel = !showKindTag
    ? null
    : event._eventKind === "candidate"
      ? "👤 Roster"
      : event._eventKind === "attribute"
        ? "📅 Availability"
        : null;

  // Username display: same fallback chain as Phase 6 diff bubbles. When
  // changedBy is "unknown" (FileMaker / external write), pull from the
  // doc's _modifitionAcct (ActiveQueue typo'd field) or _modificationAccount
  // (Candidates) — see computeScheduleDiff.js getEffectiveChangedBy. After
  // resolution, strip @domain so emails read as names. The row title
  // attribute ALSO appends the raw Lambda value when fallback kicked in
  // so support staff can tell which path produced the name.
  const effectiveChangedBy = getEffectiveChangedBy(event);
  const displayName = stripUsernameDomain(effectiveChangedBy) || "unknown";
  const rawChangedBy = event?.changedBy ?? "unknown";
  const userTitleSuffix =
    effectiveChangedBy !== rawChangedBy
      ? ` · user ${effectiveChangedBy} via FileMaker (Lambda recorded "${rawChangedBy}")`
      : "";

  return (
    <div
      data-testid="event-history-row"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      title={
        tsUtcTitle +
        " · single-click to expand · double-click to scrub time slider to this moment" +
        userTitleSuffix
      }
      className={`px-3 py-2 rounded text-sm cursor-pointer hover:bg-skin-base-hover ${
        isExpanded ? "bg-skin-base-hover" : ""
      }`}
      style={{ borderLeft: "3px solid #d4a373" }}
    >
      <div className="flex items-center gap-3 flex-wrap">
        {kindLabel ? (
          <span
            className="text-xs font-bold uppercase opacity-75 whitespace-nowrap"
            style={{ minWidth: "120px" }}
          >
            {kindLabel}
          </span>
        ) : null}
        <span className="font-mono text-xs whitespace-nowrap" style={{ minWidth: "180px" }}>
          {tsLocal}
        </span>
        <span className="font-bold whitespace-nowrap">{opLabel}</span>
        <span className="opacity-75 truncate">
          by {displayName}
          {event.source ? ` · ${event.source}` : ""}
        </span>
      </div>
      {isExpanded ? <EventRowDetail event={event} /> : null}
    </div>
  );
};

const EventRowDetail = ({ event }) => {
  // Phase 5 follow-up: compute a unified "Changed fields" summary that
  // works for events lacking trigger-supplied updateFields (e.g. FileMaker
  // replace events). The diff helper falls back to before/after comparison
  // when updateFields is missing, so this section is populated for ALL
  // events that have meaningful change data.
  const diff = computeEventDiff(event);

  return (
    <div
      data-testid="event-history-row-detail"
      className="mt-2 text-xs font-mono"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.25)",
        borderRadius: "4px",
        padding: "0.5rem",
      }}
    >
      {diff.length > 0 ? (
        <div className="mb-2" data-testid="event-history-diff-section">
          <div className="font-bold opacity-75 mb-1">Changed fields:</div>
          <ul className="ml-2">
            {diff.map(({ key, before, after }) => (
              <li key={key} className="mb-0.5 wrap-break-word">
                <span className="font-bold">{key}:</span>{" "}
                <span style={{ color: "#ffd9d9" }}>
                  {formatDiffValue(before)}
                </span>
                {" → "}
                <span style={{ color: "#d9ffd9" }}>
                  {formatDiffValue(after)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {event.before ? (
        <details
          className="mb-1"
          // Stop click propagation so toggling Before/After doesn't bubble
          // up to the parent row's onClick (which would collapse the entire
          // detail block).
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <summary className="cursor-pointer opacity-75">Before</summary>
          <pre
            className="wrap-break-word whitespace-pre-wrap"
            style={{ maxHeight: "200px", overflow: "auto" }}
          >
            {JSON.stringify(event.before, null, 2)}
          </pre>
        </details>
      ) : null}
      {event.after ? (
        <details
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <summary className="cursor-pointer opacity-75">After</summary>
          <pre
            className="wrap-break-word whitespace-pre-wrap"
            style={{ maxHeight: "200px", overflow: "auto" }}
          >
            {JSON.stringify(event.after, null, 2)}
          </pre>
        </details>
      ) : null}
      {diff.length === 0 && !event.before && !event.after ? (
        <div className="opacity-75 italic">No detail available for this event.</div>
      ) : null}
    </div>
  );
};

/**
 * Time Machine #114 Phase 6 — audit-record row variant for the History feed.
 *
 * Each BlackoutDatesAudit invocation appears as one row labelled "🌐 Customer
 * Web Portal queried availability". Single-click expands to a verbose
 * pre-formatted JSON dump of the full audit record (we'll filter later;
 * for now show everything per user spec). Double-click scrubs the time-
 * travel slider to the audit's invokedAt — supports the "what did the
 * customer see?" forensic flow: the schedule grid then shows the live
 * availability the Lambda computed against at that exact moment.
 */
const AuditRow = ({ audit, timestamp, isExpanded, onClick, onDoubleClick }) => {
  const tsDate = new Date(timestamp);
  const tsLocal = isNaN(tsDate.getTime()) ? timestamp : tsDate.toLocaleString();
  const tsUtcTitle = isNaN(tsDate.getTime()) ? "" : `UTC: ${tsDate.toISOString()}`;

  const inputs = audit?.inputs || {};
  const result = audit?.result || {};
  const blackoutCount = Array.isArray(result.blackoutDates)
    ? result.blackoutDates.length
    : 0;
  const meta = audit?.meta || {};

  return (
    <div
      data-testid="audit-history-row"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      title={
        tsUtcTitle +
        " · single-click to expand · double-click to scrub time slider to the moment the customer queried"
      }
      className={`px-3 py-2 rounded text-sm cursor-pointer hover:bg-skin-base-hover ${
        isExpanded ? "bg-skin-base-hover" : ""
      }`}
      // Distinct sepia-blue accent so audit rows stand apart from EventLog
      // rows (which use d4a373 sandy-orange).
      style={{ borderLeft: "3px solid #6dd1d8" }}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-xs whitespace-nowrap" style={{ minWidth: "180px" }}>
          {tsLocal}
        </span>
        <span className="font-bold whitespace-nowrap">
          {"\u{1F310}"} Customer Web Portal queried availability
        </span>
        <span className="opacity-75 truncate text-xs">
          {inputs.surface ? `${inputs.surface}` : ""}
          {inputs.location ? ` · ${inputs.location}` : ""}
          {blackoutCount > 0 ? ` · ${blackoutCount} blackout date${blackoutCount === 1 ? "" : "s"}` : ""}
          {meta.lambdaVersion ? ` · Lambda ${meta.lambdaVersion}` : ""}
        </span>
      </div>
      {isExpanded ? <AuditRowDetail audit={audit} /> : null}
    </div>
  );
};

const AuditRowDetail = ({ audit }) => {
  // Per user spec: "show it all because it reveals everything that we ever
  // needed to know as far as why the customer was able to schedule on the
  // date that they did". We may filter fields later; for now dump the entire
  // record verbatim. The dateScheduledArray (per-date × per-installer matrix)
  // is the load-bearing diagnostic field — preserving it.
  let pretty;
  try {
    pretty = JSON.stringify(audit, null, 2);
  } catch (e) {
    pretty = `[unprintable audit record: ${e.message}]`;
  }
  return (
    <div
      data-testid="audit-history-row-detail"
      className="mt-2 text-xs font-mono"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.25)",
        borderRadius: "4px",
        padding: "0.5rem",
      }}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <pre
        className="wrap-break-word whitespace-pre-wrap"
        style={{ maxHeight: "400px", overflow: "auto" }}
      >
        {pretty}
      </pre>
    </div>
  );
};

/**
 * Render a field value compactly for the Changed-fields list. Strings are
 * quoted; null/undefined shown as their literal name; objects/arrays
 * stringified with truncation for very long values.
 */
function formatDiffValue(value) {
  if (value === undefined) return "(missing)";
  if (value === null) return "null";
  if (typeof value === "string") {
    if (value === "") return '""';
    return `"${truncate(value, 80)}"`;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "object") {
    try {
      return truncate(JSON.stringify(value), 120);
    } catch {
      return "[unprintable]";
    }
  }
  return String(value);
}

function truncate(s, max) {
  if (typeof s !== "string") return s;
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
}

export { formatDiffValue, truncate };
export default EventHistoryList;
